/**
 * <s-media-slider> — Paged product gallery.
 *
 * Wraps a horizontally scrolling, scroll-snapped track and adds previous/next
 * controls plus a "2 / 5" counter. Native scrolling (swipe, trackpad, keyboard)
 * keeps working; the buttons only scroll the track by one slide.
 *
 * Required child markup:
 *   [data-slider-track]  — The scroll-snap container; each direct child is one slide.
 *   [data-slider-prev]   — Previous button.
 *   [data-slider-next]   — Next button.
 *   [data-slider-count]  — Optional element whose text becomes "current / total".
 */
class SMediaSlider extends HTMLElement {
  #track;
  #prev;
  #next;
  #count;
  #frame = 0;
  #controller;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    this.#prev = this.querySelector('[data-slider-prev]');
    this.#next = this.querySelector('[data-slider-next]');
    this.#count = this.querySelector('[data-slider-count]');
    if (!this.#track) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#prev?.addEventListener('click', () => this.go(-1), { signal });
    this.#next?.addEventListener('click', () => this.go(1), { signal });
    this.#track.addEventListener('scroll', () => this.#schedule(), { signal, passive: true });
    window.addEventListener('resize', () => this.#schedule(), { signal, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return Array.from(this.#track.children);
  }

  get #index() {
    const width = this.#track.clientWidth || 1;
    const offset = Math.abs(this.#track.scrollLeft);
    return Math.min(this.#slides.length - 1, Math.round(offset / width));
  }

  go(step) {
    const slides = this.#slides;
    const target = slides[Math.max(0, Math.min(slides.length - 1, this.#index + step))];
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const total = this.#slides.length;
    const index = this.#index;
    if (this.#prev) this.#prev.disabled = index <= 0;
    if (this.#next) this.#next.disabled = index >= total - 1;
    if (this.#count) this.#count.textContent = `${index + 1} / ${total}`;
  }
}

if (!customElements.get('s-media-slider')) {
  customElements.define('s-media-slider', SMediaSlider);
}
